import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useMemo, useState } from "react";
import Avatar from "@/components/Avatar";
import ProfileDialog from "@/components/ProfileDialog";
import CommandPalette from "@/components/CommandPalette";
import NewMenu from "@/components/NewMenu";
import NotificationsBell from "@/components/NotificationsBell";
import FeedbackDialog from "@/components/FeedbackDialog";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import PendingFormsGate from "@/components/PendingFormsGate";
import { api } from "@/lib/api";
import {
  LayoutDashboard, CalendarDays, AlertTriangle, StickyNote,
  FileText, ClipboardList, Settings, LogOut, Menu, X, Sparkles,
  ArrowLeftRight, PhoneCall, Star, Wallet, Command, ClipboardCheck,
  MoreHorizontal, ChevronDown, ChevronUp, Search, Megaphone, Building2, Eye,
  Users as UsersIcon, FileSignature, Wrench,
} from "lucide-react";
import { useModules } from "@/context/ModulesContext";
import { fmtDate } from "@/lib/format";

// Role-aware primary navigation, grouped into sections. Each anchor is a
// single click from Home. Everything else lives inside a hub (tabs) or the
// More… drawer. Sections give the eye a scent-trail without inflating the
// item count.
const CAST_SECTIONS = [
  { title: "Overview", items: [
    { to: "/dashboard", label: "Home", icon: LayoutDashboard },
  ]},
  { title: "Schedule", items: [
    { to: "/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/conflicts", label: "Conflicts", icon: AlertTriangle },
  ]},
  { title: "Notes", items: [
    { to: "/notes-hub", label: "My Notes", icon: StickyNote },
    { to: "/announcements", label: "Announcements", icon: Megaphone },
  ]},
  { title: "Company", items: [
    { to: "/documents", label: "Documents", icon: FileText },
    { to: "/forms", label: "Forms", icon: FileSignature },
  ]},
];

const STAFF_SECTIONS = [
  { title: "Overview", items: [
    { to: "/dashboard", label: "Home", icon: LayoutDashboard },
  ]},
  { title: "Schedule", items: [
    { to: "/calendar", label: "Calendar", icon: CalendarDays },
    { to: "/rehearsals", label: "Rehearsals", icon: ClipboardCheck, module: "rehearsal_reports" },
    { to: "/conflicts", label: "Conflicts", icon: AlertTriangle, roles: ["org_admin","production_staff","support_staff","producer","super_admin"] },
    { to: "/facilities", label: "Facilities", icon: Building2, module: "facilities" },
  ]},
  { title: "Communication", items: [
    { to: "/notes-hub", label: "Notes", icon: StickyNote },
    { to: "/announcements", label: "Announcements", icon: Megaphone },
  ]},
  { title: "Casting", items: [
    { to: "/auditions", label: "Auditions", icon: Star },
  ]},
  { title: "Company", items: [
    { to: "/people", label: "People", icon: PhoneCall },
    { to: "/documents", label: "Documents", icon: FileText },
    { to: "/forms", label: "Forms", icon: FileSignature },
  ]},
  { title: "Finances", items: [
    { to: "/budget", label: "Budget", icon: Wallet, module: "budget" },
  ]},
  { title: "Production", items: [
    { to: "/tech", label: "Tech Hub", icon: Wrench, roles: ["org_admin","production_staff","producer","support_staff","super_admin"] },
  ]},
  { title: "Configuration", items: [
    { to: "/users", label: "Company Setup", icon: UsersIcon, roles: ["org_admin","production_staff","super_admin"] },
    { to: "/settings", label: "Settings", icon: Settings, roles: ["org_admin","production_staff","super_admin"] },
  ]},
];

// Less-frequent surfaces — always accessible via the "More…" drawer or ⌘K.
const MORE_ITEMS = [
  { to: "/script", label: "Script", icon: FileText, roles: ["org_admin","production_staff","support_staff","producer","cast"], module: "script" },
  { to: "/scenes", label: "Scenes & Songs", icon: FileText, roles: ["org_admin","production_staff","support_staff","producer","cast"] },
  { to: "/reports", label: "Exports", icon: ClipboardList, roles: ["org_admin","production_staff"] },
  { to: "/email-templates", label: "Email templates", icon: Megaphone, roles: ["org_admin","production_staff"] },
  { to: "/email-log", label: "Email audit", icon: ClipboardCheck, roles: ["org_admin","production_staff","producer"] },
];

export default function Layout() {
  const { user, logout, production, selectProduction, isRealSuperAdmin, viewAsOrgAdmin, setViewAsOrgAdmin } = useAuth();
  const { modules } = useModules();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [openProfile, setOpenProfile] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [version, setVersion] = useState(null);
  useEffect(() => {
    api.get("/changelog/public").then((r) => setVersion(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault(); setCmdOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
  const impersonating =
    user?.role === "super_admin" && !!production && typeof window !== "undefined" &&
    sessionStorage.getItem("sc_impersonating") === "1";

  // Pick the role-aware primary nav. Super_admin sees staff nav for content review.
  const primarySections = useMemo(() => {
    const raw = user.role === "cast" ? CAST_SECTIONS : STAFF_SECTIONS;
    return raw
      .map((sec) => ({
        ...sec,
        items: sec.items.filter((n) => {
          if (n.module && modules?.[n.module] === false) return false;
          // Role-restricted items in the primary nav — only surface when
          // this user's effective role is on the allow-list. Super_admin
          // always sees every staff surface for support/content review.
          if (n.roles && !n.roles.includes(user.role) && user.role !== "super_admin") return false;
          return true;
        }),
      }))
      .filter((sec) => sec.items.length > 0);
  }, [user.role, modules]);

  const moreItems = useMemo(() => MORE_ITEMS.filter((m) =>
    (m.roles.includes(user.role) || user.role === "super_admin") &&
    (!m.module || modules?.[m.module] !== false)
  ), [user.role, modules]);

  const switchProduction = () => {
    sessionStorage.removeItem("sc_impersonating");
    selectProduction(null);
    nav("/productions");
  };

  const exitImpersonation = () => {
    sessionStorage.removeItem("sc_impersonating");
    selectProduction(null);
    nav("/platform/productions");
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      {/* mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-14 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gold" />
          <span className="font-display text-lg tracking-tight">StageCall</span>
        </div>
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(v => !v)} className="p-2">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* sidebar */}
      <aside
        className={`fixed md:sticky top-0 z-30 h-screen w-72 border-r border-white/10 bg-[#0A0A0A] flex flex-col
          ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 transition-transform`}
      >
        <div className="px-6 pt-6 pb-3 hidden md:block">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md border border-white/10 grid place-items-center bg-[#121212]">
              <Sparkles className="w-4 h-4 text-gold" />
            </div>
            <div>
              <div className="font-display text-xl leading-none tracking-tight">StageCall</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Production Desk</div>
            </div>
          </div>
        </div>

        {/* theatre branding (if configured) */}
        {(production?.theater_logo_url || production?.theater_name) && (
          <div className="mx-4 mb-2 rounded-md border border-white/10 bg-[#0F0F0F] p-3 flex items-center gap-3" data-testid="sidebar-theater-branding">
            {production.theater_logo_url ? (
              <img src={production.theater_logo_url} alt={production.theater_name || "Theatre"} className="w-14 h-14 rounded object-contain bg-[#0A0A0A] border border-white/10 p-0.5 shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded bg-[#0A0A0A] border border-white/10 grid place-items-center text-zinc-500 shrink-0">
                <Sparkles className="w-5 h-5" />
              </div>
            )}
            {production.theater_name && (
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">Presented by</div>
                <div className="font-display text-sm leading-tight break-words" data-testid="sidebar-theater-name">{production.theater_name}</div>
              </div>
            )}
          </div>
        )}

        {/* current production */}
        <div className="mx-4 mt-2 mb-2 md:mt-3 rounded-md border border-gold/30 bg-gradient-to-br from-[#141210] to-[#0F0F0F] p-3">
          <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">In production</div>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="font-display text-sm leading-tight break-words" data-testid="current-production-name">{production?.title || "—"}</div>
              {production?.director && <div className="text-[10px] text-zinc-500 leading-tight break-words mt-0.5">Dir. {production.director}</div>}
            </div>
            <button
              data-testid="switch-production-btn"
              onClick={switchProduction}
              title="Switch production"
              className="p-1.5 rounded border border-white/10 text-gold hover:bg-gold/10 shrink-0"
            >
              <ArrowLeftRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-2 md:pt-1 pt-16 space-y-1" data-testid="primary-nav">
          {/* + New menu — the app's create button. Fixed at the top of the rail. */}
          <div className="px-1 mb-2">
            <NewMenu />
          </div>
          <button
            data-testid="nav-search"
            onClick={() => setCmdOpen(true)}
            className="w-full mb-2 flex items-center gap-2 px-3 py-2 rounded-md bg-[#0F0F0F] border border-white/10 text-sm text-zinc-400 hover:text-white hover:border-white/20 transition-colors"
          >
            <Search className="w-3.5 h-3.5" />
            <span className="flex-1 text-left">Search or jump to…</span>
            <span className="text-[10px] uppercase tracking-widest text-zinc-500 border border-white/10 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><Command className="w-2.5 h-2.5" />K</span>
          </button>
          {primarySections.map((section, idx) => (
            <div key={section.title} data-testid={`nav-section-${section.title.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className={`px-3 mb-1.5 text-[10px] uppercase tracking-[0.24em] text-zinc-500 ${idx === 0 ? "" : "pt-3"}`}>
                {section.title}
              </div>
              {section.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-${to.replace("/","")}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2.5 rounded-md text-sm mb-0.5 transition-all
                     ${isActive
                        ? "bg-[#121212] text-white border-l-2 border-gold"
                        : "text-zinc-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent"}`
                  }
                >
                  <Icon className="w-4 h-4 shrink-0" />
                  <span className="truncate">{label}</span>
                </NavLink>
              ))}
            </div>
          ))}

          {moreItems.length > 0 && (
            <div className="pt-2" data-testid="more-drawer">
              <button
                data-testid="more-drawer-toggle"
                onClick={() => setMoreOpen((v) => !v)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-sm text-zinc-400 hover:text-white hover:bg-white/5 border-l-2 border-transparent transition-all"
              >
                <MoreHorizontal className="w-4 h-4 shrink-0" />
                <span className="flex-1 text-left">More…</span>
                {moreOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>
              {moreOpen && (
                <div className="mt-1 ml-2 border-l border-white/5 pl-2 space-y-0.5">
                  {moreItems.map(({ to, label, icon: Icon }) => (
                    <NavLink
                      key={to}
                      to={to}
                      onClick={() => setOpen(false)}
                      data-testid={`more-nav-${to.replace("/","")}`}
                      className={({ isActive }) =>
                        `flex items-center gap-2.5 px-2.5 py-2 rounded text-xs transition-all
                         ${isActive
                            ? "bg-[#121212] text-white"
                            : "text-zinc-500 hover:text-white hover:bg-white/5"}`
                      }
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="p-4 border-t border-white/10">
          {isRealSuperAdmin && (
            <button
              data-testid="view-as-org-admin-toggle"
              onClick={() => setViewAsOrgAdmin(!viewAsOrgAdmin)}
              className={`w-full mb-3 flex items-center gap-2 justify-center rounded-md px-3 py-2 text-sm transition-colors border ${
                viewAsOrgAdmin
                  ? "bg-amber-500 text-black border-amber-500 hover:brightness-105 font-semibold"
                  : "border-white/15 text-zinc-300 hover:text-white hover:bg-white/5"
              }`}
              title={viewAsOrgAdmin ? "Currently seeing what the client sees" : "See exactly what an Org Admin sees"}
            >
              <Eye className="w-4 h-4" /> {viewAsOrgAdmin ? "Exit view-as Org Admin" : "View as Org Admin"}
            </button>
          )}
          {(user.role === "org_admin" || user.role === "super_admin") && (
            <button
              data-testid="goto-admin-dashboard-btn"
              onClick={() => nav("/admin/dashboard")}
              className="w-full mb-3 flex items-center gap-2 justify-center border border-gold/40 hover:border-gold rounded-md px-3 py-2 text-sm text-gold hover:bg-gold/10 transition-colors"
            >
              <LayoutDashboard className="w-4 h-4" /> Admin Dashboard
            </button>
          )}
          {user.role === "super_admin" && (
            <button
              data-testid="goto-platform-btn"
              onClick={() => nav("/platform/overview")}
              className="w-full mb-3 flex items-center gap-2 justify-center border border-gold/40 hover:border-gold rounded-md px-3 py-2 text-sm text-gold hover:bg-gold/10 transition-colors"
            >
              <Sparkles className="w-4 h-4" /> Platform Admin
            </button>
          )}
          <div className="flex items-center gap-2 mb-3">
          <button
            data-testid="open-profile-btn"
            onClick={() => setOpenProfile(true)}
            className="flex-1 flex items-center gap-3 mb-3 hover:bg-white/5 rounded-md p-1 -m-1 transition-colors text-left min-w-0"
          >
            <Avatar user={user} size="w-9 h-9" refresh={user?.avatar_path || 0} />
            <div className="min-w-0 flex-1">
              <div className="text-sm truncate" data-testid="sidebar-user-name">{user.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500" data-testid="sidebar-user-role">{user.role}</div>
            </div>
          </button>
          <NotificationsBell />
          <FeedbackDialog />
          </div>
          <button
            data-testid="logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="w-full flex items-center gap-2 justify-center border border-white/15 hover:border-white/30 rounded-md px-3 py-2 text-sm text-zinc-300 hover:text-white transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
          {version && (
            <button
              data-testid="sidebar-version-badge"
              onClick={() => (user.role === "org_admin" || user.role === "super_admin") && nav("/changelog")}
              disabled={!(user.role === "org_admin" || user.role === "super_admin")}
              className={`mt-3 w-full text-center text-[10px] uppercase tracking-[0.22em] transition-colors
                ${user.role === "org_admin" || user.role === "super_admin"
                  ? "text-zinc-500 hover:text-gold cursor-pointer"
                  : "text-zinc-600 cursor-default"}`}
              title={user.role === "org_admin" || user.role === "super_admin" ? "View changelog" : ""}
            >
              StageCall {version.version}{version.date ? ` · ${fmtDate(version.date)}` : ""}
            </button>
          )}
        </div>
      </aside>

      <ProfileDialog open={openProfile} onOpenChange={setOpenProfile} />
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />

      <main className="flex-1 md:ml-0 mt-14 md:mt-0 min-w-0">
        <ImpersonationBanner />
        <PendingFormsGate />
        {impersonating && (
          <div
            data-testid="impersonation-banner"
            className="sticky top-0 z-20 border-b border-gold/40 bg-gradient-to-r from-gold/20 to-gold/10 backdrop-blur px-4 md:px-8 py-2.5 flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="w-4 h-4 text-gold shrink-0" />
              <div className="text-xs text-zinc-100 truncate">
                <span className="uppercase tracking-widest text-gold text-[10px] mr-2">Super admin</span>
                Viewing <span className="font-medium">{production?.title}</span>
                {production?.theater_name && <span className="text-zinc-400"> · {production.theater_name}</span>}
              </div>
            </div>
            <button
              data-testid="exit-impersonation-btn"
              onClick={exitImpersonation}
              className="shrink-0 h-7 px-3 rounded border border-gold/40 text-gold text-[11px] hover:bg-gold/10 inline-flex items-center gap-1"
            >
              <ArrowLeftRight className="w-3 h-3" /> Exit to Platform
            </button>
          </div>
        )}
        <div className="max-w-[1400px] mx-auto p-6 md:p-10">
          <Outlet />
          {(user.role === "org_admin" || user.role === "super_admin") && version && (
            <footer
              data-testid="page-footer-changelog"
              className="mt-16 pt-6 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3 text-[11px] text-zinc-500"
            >
              <div>
                StageCall <span className="text-zinc-400">{version.version}</span>
                {version.date && <span className="text-zinc-600"> · updated {fmtDate(version.date)}</span>}
              </div>
              <NavLink
                to="/changelog"
                data-testid="page-footer-changelog-link"
                className="inline-flex items-center gap-1.5 text-gold hover:brightness-110 uppercase tracking-[0.22em]"
              >
                View version updates &amp; feedback →
              </NavLink>
            </footer>
          )}
        </div>
      </main>
    </div>
  );
}
